// project_details editor — PROP-68 / S5.
//
// Lives as a section under the floorplan inspector. URA-canonical
// fields render read-only with a "URA-canonical" badge; only the cos
// specs_parsed whitelist is editable. URA strings come back UPPERCASE
// from the DB (URA ingest convention) — we title-case at the display
// boundary, never mutating the raw row.
//
// On save, sends a flat PATCH map (whitelisted keys only) + reason.
// Empty text input → JSON null → server clears the key from
// specs_parsed. Numeric clears require selecting the key's "clear"
// checkbox (numerics can't piggy-back on empty-string semantics).

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { api, ApiError } from "@/lib/api";
import { titleCase as titleCaseShared } from "@/lib/format";
import { cardWrap, hdrCell, ghostBtn, primaryBtn } from "@/components/admin-ui";
import { inputBase, useToast } from "@/components/ui-prims";

// Keep in step with backend projectDetailsWhitelist. The backend
// returns its own list on every GET so the FE is loosely coupled, but
// we still need the per-key UI metadata locally.
const COS_FIELDS: { key: string; label: string; kind: "text" | "int"; hint?: string }[] = [
  { key: "developer",       label: "Developer",        kind: "text" },
  { key: "tenure_cos",      label: "Tenure (cos)",     kind: "text", hint: "Plain string — cos.sg phrasing, distinct from URA tenure." },
  { key: "top_year_cos",    label: "TOP year (cos)",   kind: "int",  hint: "1960–2099" },
  { key: "no_of_units_cos", label: "Unit count (cos)", kind: "int",  hint: "1–10000" },
  { key: "unit_types_text", label: "Unit types text",  kind: "text", hint: "Free-text label string from cos.sg." },
];

type BundleResp = {
  slug: string;
  details: {
    title: string;
    url: string;
    category?: string;
    specs_parsed: Record<string, unknown>;
    ingested_at: string;
    synced_at: string;
  } | null;
  ura: {
    name: string;
    street?: string | null;
    district?: string | null;
    tenure?: string | null;
    market_segment?: string | null;
    postal?: string | null;
    property_types?: string[];
    first_txn_date?: string | null;
    last_txn_date?: string | null;
    txn_count?: number | null;
    updated_at: string;
  } | null;
  whitelist: string[];
};

interface Props {
  slug: string;
  onSaved?: () => void;
}

export function ProjectDetailsSection({ slug, onSaved }: Props) {
  const toast = useToast();
  const [data, setData] = useState<BundleResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const fetchBundle = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get<BundleResp>(
        `/admin/dataengine/reconcile/project_details?slug=${encodeURIComponent(slug)}`,
      );
      setData(resp);
      setErr(null);
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 404) {
        setData(null);
        setErr("no_project_details");
      } else {
        setErr(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchBundle(); }, [fetchBundle]);

  if (loading && !data) {
    return (
      <div style={{ ...cardWrap, margin: 0, padding: "16px 14px", color: "var(--ink-3)", fontStyle: "italic", fontSize: 12 }}>
        Loading project details…
      </div>
    );
  }
  if (err === "no_project_details") {
    return (
      <div style={{ ...cardWrap, margin: 0, padding: "16px 14px", color: "var(--ink-3)", fontStyle: "italic", fontSize: 12 }}>
        No <code>project_details</code> row for this slug yet — the cos.sg ingest hasn't reached it.
      </div>
    );
  }
  if (err || !data || !data.details) {
    return (
      <div style={{ ...cardWrap, margin: 0, padding: "16px 14px", color: "var(--neg)", fontSize: 12 }}>
        {err || "Project details unavailable."}
      </div>
    );
  }

  return (
    <div style={{ ...cardWrap, margin: 0 }}>
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={hdrCell}>Project details · cos + URA</div>
        <a
          href={data.details.url}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 11, color: "var(--accent)", textDecoration: "none" }}
        >
          source ↗
        </a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)", gap: 0 }}>
        <URAPanel ura={data.ura} />
        <CosForm
          slug={slug}
          specs={data.details.specs_parsed}
          onSaved={() => { fetchBundle(); onSaved?.(); toast("project_details updated.", { tone: "success" }); }}
          onError={(msg) => toast(msg, { tone: "error" })}
        />
      </div>
    </div>
  );
}

/* ─── URA read-only panel ──────────────────────────────────────────── */

function URAPanel({ ura }: { ura: BundleResp["ura"] }) {
  if (!ura) {
    return (
      <div style={{ padding: "12px 14px", borderRight: "1px solid var(--rule)", background: "var(--surface-2)", fontSize: 12, color: "var(--ink-3)", fontStyle: "italic", minHeight: 200 }}>
        No URA-canonical projects row for this slug. The cos↔URA matcher
        (tier 1+2) hasn't bound it yet — investigate via the reconcile queue.
      </div>
    );
  }
  return (
    <div style={{ padding: "12px 14px", borderRight: "1px solid var(--rule)", background: "var(--surface-2)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span style={uraBadge}>URA-canonical</span>
        <span style={{ fontSize: 10.5, color: "var(--ink-4)" }}>
          read-only · updated {fmtDate(ura.updated_at)}
        </span>
      </div>
      <ReadField label="Name"            value={titleCase(ura.name)} />
      <ReadField label="District"        value={titleCase(ura.district)} />
      <ReadField label="Street"          value={titleCase(ura.street)} />
      <ReadField label="Postal"          value={ura.postal /* keep digits as-is */} />
      <ReadField label="Tenure (URA)"    value={titleCase(ura.tenure)} />
      <ReadField label="Market segment"  value={titleCase(ura.market_segment)} />
      <ReadField label="Property types"  value={ura.property_types?.length ? ura.property_types.map(titleCase).join(", ") : null} />
      <ReadField label="Txn count"       value={ura.txn_count == null ? null : String(ura.txn_count)} />
      <ReadField label="First txn"       value={ura.first_txn_date} />
      <ReadField label="Last txn"        value={ura.last_txn_date} />
    </div>
  );
}

function ReadField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 8, padding: "4px 0", alignItems: "baseline" }}>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: value == null || value === "" ? "var(--ink-4)" : "var(--ink)" }}>
        {value == null || value === "" ? "—" : value}
      </div>
    </div>
  );
}

const uraBadge: CSSProperties = {
  display: "inline-flex", alignItems: "center",
  fontFamily: "var(--font-ui)", fontSize: 10, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase",
  padding: "2px 8px", borderRadius: 4,
  background: "rgba(29, 74, 58, 0.10)", color: "#1d4a3a",
  border: "1px solid rgba(29, 74, 58, 0.25)",
};

/* ─── cos editable form ────────────────────────────────────────────── */

function CosForm({ slug, specs, onSaved, onError }: {
  slug: string;
  specs: Record<string, unknown>;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  // Snapshot the current value per whitelisted key. Numerics may come
  // back as numbers or numeric strings; coerce to strings for the
  // <input> bindings.
  const initial = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const f of COS_FIELDS) {
      const v = specs?.[f.key];
      out[f.key] = v == null ? "" : String(v);
    }
    return out;
  }, [specs]);

  const [draft, setDraft] = useState<Record<string, string>>(initial);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset draft state when the upstream snapshot changes (after a save).
  useEffect(() => { setDraft(initial); setReason(""); }, [initial]);

  // Diff against the snapshot. For each whitelisted key:
  //   - text: trimmed value differs from initial → include
  //   - int : parsed value differs from initial → include
  // Empty text input is sent as JSON null (clear); empty numeric input
  // is sent as null only if the original had a value (clear-by-omission).
  const patchBody = useMemo<Record<string, any>>(() => {
    const body: Record<string, any> = {};
    for (const f of COS_FIELDS) {
      const cur = draft[f.key] ?? "";
      const orig = initial[f.key] ?? "";
      if (cur === orig) continue;
      if (cur.trim() === "") {
        body[f.key] = null;
        continue;
      }
      if (f.kind === "int") {
        const n = parseInt(cur, 10);
        if (!Number.isNaN(n)) body[f.key] = n;
      } else {
        body[f.key] = cur.trim();
      }
    }
    return body;
  }, [draft, initial]);

  const changedKeys = Object.keys(patchBody);
  const reasonOk = reason.trim().length > 0;
  const submittable = !busy && changedKeys.length > 0 && reasonOk;

  async function submit() {
    if (!submittable) return;
    setBusy(true);
    try {
      await api.patch(
        `/admin/dataengine/reconcile/project_details/${encodeURIComponent(slug)}`,
        { ...patchBody, reason: reason.trim() },
      );
      onSaved();
    } catch (e: any) {
      onError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span style={cosBadge}>cos.sg specs_parsed</span>
        <span style={{ fontSize: 10.5, color: "var(--ink-4)" }}>editable · whitelist only</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {COS_FIELDS.map((f) => (
          <label key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={fieldLabel}>
              {f.label}
              {f.hint && <span style={hintInline}> · {f.hint}</span>}
            </span>
            <input
              style={inputBase}
              type={f.kind === "int" ? "number" : "text"}
              min={f.kind === "int" ? 0 : undefined}
              value={draft[f.key] ?? ""}
              onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
              placeholder="—"
              disabled={busy}
            />
          </label>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={fieldLabel}>Reason *</span>
          <input
            style={inputBase}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. cos.sg shows 220 units but brochure says 248 — correcting"
            disabled={busy}
          />
        </label>
      </div>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>
          {changedKeys.length === 0
            ? "No changes yet."
            : `Will patch: ${changedKeys.join(", ")}`}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            style={ghostBtn}
            onClick={() => { setDraft(initial); setReason(""); }}
            disabled={busy || changedKeys.length === 0}
          >Reset</button>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: submittable ? 1 : 0.5, cursor: submittable ? "pointer" : "not-allowed" }}
            onClick={submit}
            disabled={!submittable}
          >
            {busy ? "Saving…" : "Save cos details"}
          </button>
        </div>
      </div>
    </div>
  );
}

const cosBadge: CSSProperties = {
  display: "inline-flex", alignItems: "center",
  fontFamily: "var(--font-ui)", fontSize: 10, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase",
  padding: "2px 8px", borderRadius: 4,
  background: "rgba(122, 45, 31, 0.10)", color: "#7a2d1f",
  border: "1px solid rgba(122, 45, 31, 0.25)",
};
const fieldLabel: CSSProperties = {
  fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600,
  letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-4)",
};
const hintInline: CSSProperties = {
  textTransform: "none", letterSpacing: 0, fontWeight: 400, color: "var(--ink-4)",
};

/* ─── Display-side helpers ─────────────────────────────────────────── */

// titleCase wraps the shared util to preserve null pass-through — read
// fields elsewhere on this page accept null and render "—".
function titleCase(s?: string | null): string | null {
  if (s == null) return null;
  return titleCaseShared(s);
}

function fmtDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "2-digit" });
}
